import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ConfirmationModal from '../../Modal/ConfirmationModal';
import createNotification from '../../../utils/createNotification';
import { ConsiderationSection } from '../../../models/FlowOfFunds/flow-of-funds.model';
import { GetFlowOfFunds } from '../../../slices/marketplace/flow-of-funds/thunks/get-flow-of-funds.thunk';
import {
    RequestConsiderationRowRemoval,
    RequestConsiderationRowRemovalRejection,
} from '../../../slices/marketplace/flow-of-funds/thunks/request-consideration-row-removal.thunk';
import { useConsiderationAutoSaveReporter } from './ConsiderationAutoSaveContext';
import { useConsiderationAutosaveQueue } from './ConsiderationAutosaveQueueContext';

const CONFLICT_MESSAGE = 'Someone else changed this row. The latest values have been loaded.';

export interface ConsiderationRemovalActionProps {
    canRequestRemoval: boolean;
    label: string;
    section: ConsiderationSection;
    rowKey: number;
    engagementId: number;
}

export const ConsiderationRemovalAction = ({
    canRequestRemoval,
    label,
    section,
    rowKey,
    engagementId,
}: ConsiderationRemovalActionProps) => {
    const dispatch = useDispatch() as any;
    const reporter = useConsiderationAutoSaveReporter();
    const queue = useConsiderationAutosaveQueue();
    const [isConfirming, setIsConfirming] = useState(false);
    const [isRequesting, setIsRequesting] = useState(false);

    if (!canRequestRemoval) {
        return null;
    }

    const requestRemoval = async () => {
        setIsRequesting(true);
        const version = await queue.flushRowForRemoval(section, rowKey);
        reporter.reportStart();
        const action = await dispatch(
            RequestConsiderationRowRemoval({ engagementId, section, rowKey, version })
        );
        if (RequestConsiderationRowRemoval.fulfilled.match(action)) {
            reporter.reportOutcome('ok');
        } else {
            const rejection = action.payload as RequestConsiderationRowRemovalRejection | undefined;
            if (rejection?.isConflict) {
                createNotification(CONFLICT_MESSAGE, 'error');
                dispatch(GetFlowOfFunds(engagementId));
                reporter.reportOutcome('conflict');
            } else {
                reporter.reportOutcome('error');
            }
        }
        setIsRequesting(false);
        setIsConfirming(false);
    };

    return (
        <>
            <button
                type="button"
                className="ff-detail__remove-btn"
                aria-label="Request removal"
                title="Request removal"
                disabled={isRequesting}
                onClick={() => setIsConfirming(true)}>
                <FontAwesomeIcon icon={faTrashCan} />
            </button>
            <ConfirmationModal
                isVisible={isConfirming}
                setIsVisible={setIsConfirming}
                header="Request Removal"
                confirmationText={`Are you sure you want to request removal of "${label}"?`}
                onConfirm={requestRemoval}
                closeOnConfirm={false}
                isLoading={isRequesting}
                nextButtonLoaderText="Requesting…"
                isConfirmationDanger={true}
                cancelButtonText="No, take me back"
                nextButtonText="Yes, request removal"
            />
        </>
    );
};
